import { DestroyRef, Inject, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  BehaviorSubject,
  catchError,
  defer,
  finalize,
  from,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  tap,
  take,
  throwError,
  Subscription,
} from 'rxjs';
import { BaseService } from 'src/app/core/services/base.httpService';
import { URLConstants } from 'src/app/shared/utility/url-constants';
import {
  MSAL_GUARD_CONFIG,
  MsalGuardConfiguration,
  MsalService,
} from '@azure/msal-angular';
import { LocalStorageService } from './local-storage.service';
import { SessionStorageService } from './session-storage.service';
import { loginRequest } from '../../auth-config';
import {
  AccountInfo,
  AuthenticationResult,
  InteractionType,
  PopupRequest,
  RedirectRequest,
} from '@azure/msal-browser';
import { ErrorMessages, popupWidth, STORAGE_KEYS, StatusCodes } from '../utility/constants';
import { MessagePopupComponent } from '../components/message-popup/message-popup.component';
import { ModalService } from './modal.service';
import { getJWTPayload } from '../utility/jwt.util';
import { SpinnerService } from './spinner.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandlerService } from 'src/app/core/services/error-handler.service';
import { LocalStorageKeyService } from './local-storage-key.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public isUserDetailsChanged = new BehaviorSubject<boolean>(false);

  private warningPopup = false;
  private loginSubscription?: Subscription;
  private readonly destroyRef = inject(DestroyRef);
  private readonly inflightGetTokenWithIpByAdToken = new Map<string, Observable<unknown>>();
  private cachedIpAddress: string | null = null;
  private readonly isLoggedInBehaviorSubject = new BehaviorSubject<boolean>(
    !!this.getJWTToken()
  );

  constructor(
    public baseService: BaseService,
    private readonly msalService: MsalService,
    private readonly localStorageService: LocalStorageService,
    private readonly sessionStorageService: SessionStorageService,
    @Inject(MSAL_GUARD_CONFIG)
    private readonly msalGuardConfig: MsalGuardConfiguration,
    private readonly modalService: ModalService,
    private readonly spinnerService: SpinnerService,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly localStorageKeyService: LocalStorageKeyService
  ) {}

  get isLoggedIn$(): Observable<boolean> {
    return this.isLoggedInBehaviorSubject.asObservable();
  }

  refreshSession() {
    this.tryInitLocalStorageEncryptionKey();

    const currentUserAccount = this.msalService.instance.getActiveAccount();

    if (!currentUserAccount) {
      return of(null);
    }
    const request = {
      ...loginRequest,
      account: currentUserAccount,
    };

    return this.msalService.initialize().pipe(
      switchMap(() =>
        defer(() => {
          this.spinnerService.showSpinner();
          return from(
            this.msalService.instance.acquireTokenSilent(request)
          ).pipe(
            switchMap(response =>
              this.getTokenWithIp(response.accessToken)
            ),
            finalize(() => this.spinnerService.hideSpinner())
          );
        })
      ),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  getJWTToken(): string | null {
    const token = this.localStorageService.getItem(STORAGE_KEYS.JWT_TOKEN);
    return token ? JSON.parse(token) : null;
  }

  setJWTToken(token: string): void {
    this.localStorageService.setItem(STORAGE_KEYS.JWT_TOKEN, token);
  }

  getTokenInfo(): AccountInfo | null {
    return this.localStorageService.getParsedItem(STORAGE_KEYS.TOKEN_INFO);
  }

  setTokenInfo(info: AccountInfo): void {
    this.localStorageKeyService.initializeFromAccount(info, environment.clientId);
    this.localStorageService.setItem(STORAGE_KEYS.TOKEN_INFO, info);
  }

  public tryInitLocalStorageEncryptionKey(): boolean {
    return this.localStorageKeyService.initializeFromMsalInstance(
      this.msalService.instance,
      environment.clientId
    );
  }

  public migratePlainStorageKeys(): void {
    this.localStorageKeyService.migratePlainKeys();
  }

  getDecodedAccessToken(): Observable<{
    firstName: string;
    lastName: string;
    userName: string;
    email: string;
    accountName: string;
  }> {
    const token = this.getJWTToken();
    if (token) {
      const tokenResp = of(getJWTPayload(token));
      this.isLoggedInBehaviorSubject.next(true);
      return tokenResp;
    }

    return this.refreshSession().pipe(
      map(() => getJWTPayload(this.getJWTToken()!)),
      catchError(() => of(null)),
      tap(() => this.isLoggedInBehaviorSubject.next(!!this.getJWTToken()))
    );
  }

  showWarningPopupAndLogout(message = ErrorMessages.sessionExpiredMessage): boolean {
    if (this.warningPopup) {
      return false;
    }

    this.spinnerService.hideSpinner();

    const dialogRef = this.modalService.openPopup(
      {
        isFooterRequired: true,
        isForWarning: true,
        message,
      },
      '',
      MessagePopupComponent,
      popupWidth.warningPopup
    );
    this.warningPopup = true;
    dialogRef.afterClosed().pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe(data => {
      this.warningPopup = false;
      if (data === true) {
        this.initiateLogout({ reloginAfterLogout: true });
      }
    });
    return true;
  }

  initiateLogout(options?: { reloginAfterLogout?: boolean }): void {
    const relogin = options?.reloginAfterLogout === true;
    const activeAccount =
      this.msalService.instance.getActiveAccount() ??
      this.msalService.instance.getAllAccounts()[0];

    if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
      if (relogin) {
        this.msalService.logoutPopup({ account: activeAccount }).pipe(take(1)).subscribe({
          next: () => {
            this.clearState();
            this.login(loginRequest);
          },
          error: () => {
            this.clearState();
            this.login(loginRequest);
          },
        });
      } else {
        this.msalService.logoutPopup({
          account: activeAccount,
        });
        this.clearState();
      }
      return;
    }

    const redirectRequest: {
      account: AccountInfo | undefined;
      postLogoutRedirectUri?: string;
    } = { account: activeAccount };
    if (relogin) {
      // App root reload runs AppComponent.b2cAuthorizeRequest → login() when URL contains login=
      redirectRequest.postLogoutRedirectUri = `${window.location.origin}/?login=1`;
    }

    this.msalService.logoutRedirect(redirectRequest).pipe(take(1)).subscribe({
      error: () => this.clearState(),
    });
    this.clearState();
  }

  endSessionSilent() {
    this.msalService.logoutRedirect({
      // do not navigate once the user is logged out successfully
      onRedirectNavigate: () => false,
    });
    this.clearState();
  }

  login(userFlowRequest?: RedirectRequest | PopupRequest) {
    this.spinnerService.showSpinner();
    if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
      this.loginSubscription?.unsubscribe();
      const loginRequest$ = this.msalGuardConfig.authRequest
        ? this.msalService.loginPopup({
            ...this.msalGuardConfig.authRequest,
            ...userFlowRequest,
          } as PopupRequest)
        : this.msalService.loginPopup(userFlowRequest);

      this.loginSubscription = loginRequest$
        .pipe(take(1), takeUntilDestroyed(this.destroyRef))
        .subscribe((response: AuthenticationResult) => {
          this.msalService.instance.setActiveAccount(response.account);
        });
    } else if (this.msalGuardConfig.authRequest) {
      this.msalService.loginRedirect({
        ...this.msalGuardConfig.authRequest,
        ...userFlowRequest,
      } as RedirectRequest);
    } else {
      this.msalService.loginRedirect(userFlowRequest);
    }
  }

  public authorizeB2CToken(
    accessToken: string,
    ipAddress: string
  ): Observable<any> {
    const url = URLConstants.auth.authorize;
    return this.baseService.authorizeToken(url, accessToken, ipAddress);
  }

  public loginWithMsalForLabelApiDocs(): Observable<void> {
    const req = { ...loginRequest } as PopupRequest;

    return this.msalService.initialize().pipe(
      switchMap(() => from(this.msalService.instance.loginPopup(req))),
      tap((response: AuthenticationResult) => {
        this.msalService.instance.setActiveAccount(response.account);
      }),
      switchMap((response: AuthenticationResult) =>
        this.getTokenWithIp(response.accessToken).pipe(map(() => undefined))
      )
    );
  }

  public getIpAddressOfUSer(): Observable<any> {
    if (this.cachedIpAddress) {
      return of({ ip: this.cachedIpAddress });
    }
    // ipify (api.ipify.org) disabled temporarily — external lookup unavailable
    this.cachedIpAddress = 'unknown';
    return of({ ip: this.cachedIpAddress });
  }

  public getTokenWithIp(ADToken: string): Observable<unknown> {
    const dedupeKey = ADToken;
    const existing = this.inflightGetTokenWithIpByAdToken.get(dedupeKey);
    if (existing) {
      return existing;
    }

    const pipeline = this.getIpAddressOfUSer().pipe(
      map(({ ip }) => ({
        ip,
        accessToken: ADToken,
      })),
      switchMap(response =>
        this.authorizeB2CToken(response.accessToken ?? '', response.ip).pipe(
          switchMap(({ accessToken }) => {
            const account =
              this.msalService.instance.getActiveAccount() ??
              this.msalService.instance.getAllAccounts()[0];
            if (account) {
              this.setTokenInfo(account);
            }
            this.setJWTToken(accessToken);
            this.isLoggedInBehaviorSubject.next(true);
            return this.getUserProfileDetails().pipe(
              catchError(error => {
                console.error('Failed to fetch user profile details:', error);
                return of(null);
              })
            );
          })
        )
      ),
      finalize(() => {
        this.inflightGetTokenWithIpByAdToken.delete(dedupeKey);
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.inflightGetTokenWithIpByAdToken.set(dedupeKey, pipeline);
    return pipeline;
  }

  public getUserProfileDetails(): Observable<any> {
    return this.baseService
      .initiateApiRequest('GET', URLConstants.getUserProfileDetails, '', 'json')
      .pipe(
        tap((response: any) => {
          if (response.statusCode === StatusCodes.Success) {
            response.data.fullName =
              response.data.firstName + ' ' + response.data.lastName;
            localStorage.setItem('userDetails', JSON.stringify(response.data));
            this.userDetailsChanged(true);
          }
        }),
        catchError((error: HttpErrorResponse) => {
          this.errorHandlerService.handleError(error);
          return throwError(() => error);
        })
      );
  }

  public userDetailsChanged(data: boolean) {
    this.isUserDetailsChanged.next(data);
  }

  private clearState(): void {
    this.loginSubscription?.unsubscribe();
    this.loginSubscription = undefined;
    this.inflightGetTokenWithIpByAdToken.clear();
    this.cachedIpAddress = null;
    this.warningPopup = false;
    this.localStorageKeyService.clear();
    this.sessionStorageService.clear();
    this.localStorageService.clear();
    this.isLoggedInBehaviorSubject.next(false);
  }
}
